'use client';

import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { Link, type LinkProps } from '@ironhack/design-system2/components/link';
import { SimpleGrid } from '@ironhack/design-system2/components/grid';
import { Pagination } from '@ironhack/design-system2/components/pagination';
import { Text } from '@ironhack/design-system2/components/text';
import Head from 'next/head';
import NextLink from 'next/link';
import Script from 'next/script';
import { assoc, pick, pipe } from 'ramda';
import { type ReactElement } from 'react';
import { type BlogPosting, type ItemList, type WithContext } from 'schema-dts';

import {
  ArticleCard,
  type ArticleCardData,
} from '@/app/components/client/article-card';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { getPageUrl } from '@/app/urls/url-mapping';
import { getBaseUrlFromPaginationUrl } from '@/lib/utils';
import { type ModuleData } from '../../blog-author.queries';
import { type BlogAuthorParams } from '../../page';

type Props = {
  data: ModuleData & { articlesPerPage: number };
  params: BlogAuthorParams;
} & FlexProps;

export const ArticlesList = (props: Props): ReactElement => {
  const { data, params, ...flexProps } = props;
  const { region, language, pagination: paginationParams } = params;

  const { author, blogAuthorPage, articlesPerPage } = data;
  const {
    articlesList: [{ articleButtonText, notFoundText, browseArticlesText }],
  } = blogAuthorPage;

  const { articles, articlesInfo } = author;
  const totalArticles = articlesInfo.count;
  const has24ArticlesPerPage = articlesPerPage === 24;

  const formattedArticles: ArticleCardData[] = articles.map(
    pipe(
      pick([
        'author',
        'body',
        'coverImage',
        'publicationDate',
        'readingTime',
        'series',
        'slug',
        'subtitle',
        'title',
      ]),
      assoc('buttonText', articleButtonText),
      assoc('gaEvent', {
        eventAction: 'a',
        eventCategory: 's',
        eventLabel: 'l',
      }),
      assoc('size', [
        '2xl',
        null,
        null,
        null,
        has24ArticlesPerPage ? '3xl' : 'l',
      ])
    )
  );

  const page = paginationParams?.[paginationParams?.indexOf('page') + 1] ?? '1';
  const showPagination = totalArticles > articlesPerPage;
  const activePage = Number.parseInt(page);
  const pagesCount = Math.ceil(totalArticles / articlesPerPage);

  const pathname = getPageUrl('blogAuthor', params) as string;
  const pagination = {
    activePage,
    pagesCount,
    getPageUrl: (pageNumber: number): string => {
      const pageUrl = new URL(
        `${getBaseUrlFromPaginationUrl(pathname)}${
          pageNumber > 1 ? `/page/${pageNumber}` : ''
        }`,
        'https://example.org'
      );

      return pageUrl.href.replace('https://example.org', '');
    },
    LinkComponent: ({
      ariaLabel,
      children,
      href,
      rel,
      style,
    }: React.PropsWithChildren<
      {
        ariaLabel?: string;
      } & Pick<LinkProps, 'style' | 'rel' | 'href'>
    >) => (
      <Link
        aria-label={ariaLabel}
        as={NextLink}
        href={href}
        rel={rel}
        style={style}
      >
        {children}
      </Link>
    ),
  };

  // Consider using <script> if this ItemList is impactful to SEO
  const seoStructuredData: WithContext<ItemList> = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: formattedArticles.map(
      (article: ArticleCardData): BlogPosting => ({
        '@type': 'BlogPosting',
        author: [
          {
            '@type': 'Person',
            jobTitle: article.author.jobTitle,
            name: article.author.name,
          },
        ],
        dateModified: new Date(article.publicationDate).toISOString(),
        datePublished: new Date(article.publicationDate).toISOString(),
        headline: article.title,
        image: [article.coverImage?.responsiveImage?.src ?? ''],
      })
    ),
  };
  const prevPagination = activePage > 2 && {
    pagination: [String(activePage - 1)],
  };

  return (
    <>
      <Head>
        {activePage > 1 && (
          <link
            href={getPageUrl(
              'blogAuthor',
              { language, region, ...prevPagination },
              { withOrigin: true }
            )}
            rel="prev"
          />
        )}
        {activePage < pagesCount && (
          <link
            href={getPageUrl(
              'blogAuthor',
              { language, region, pagination: [String(activePage + 1)] },
              { withOrigin: true }
            )}
            rel="next"
          />
        )}
      </Head>
      <ModuleWrapper
        innerProps={{ gap: [2, null, 3], direction: 'column', ...flexProps }}
        moduleFormat={{
          background: 'light',
          spacingBottom: '40',
          spacingTop: '40',
        }}
      >
        <Flex w="full">
          <Text as="h2" color="text.primary" textStyle={['3xl', null, '4xl']}>
            {`${browseArticlesText} ${author.name}`}
          </Text>
        </Flex>
        <SimpleGrid
          columns={[1, null, 2, null, has24ArticlesPerPage ? 3 : 4]}
          justifyItems="center"
          spacing={[2, null, 3]}
        >
          {formattedArticles.length === 0 ? (
            <Text
              color="gray.500"
              fontSize={['xl', null, '3xl']}
              gridColumn={['span 1', null, 'span 4']}
            >
              {notFoundText}
            </Text>
          ) : (
            formattedArticles.map((article) => (
              <ArticleCard card={article} key={article.slug} />
            ))
          )}
        </SimpleGrid>
        {showPagination && (
          <Pagination {...pagination} justifyContent="center" w="full" />
        )}
      </ModuleWrapper>
      <Script
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(seoStructuredData),
        }}
        id="structured-data-author-article-list"
        strategy="lazyOnload"
        type="application/ld+json"
      />
    </>
  );
};
